import { defineComponent as _defineComponent } from 'vue'
import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import type {
	CreateTextRecommendationAndProfileMutation,
	CreateTextRecommendationAndProfileMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/CreateTextRecommendationAndProfile.mutation';
import type {
	UpdateTextRecommendationMutation,
	UpdateTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/UpdateTextRecommendation.mutation';

import { CreateTextRecommendationAndProfileDocument } from '@/features/why-to-watch/graphql/mutations/CreateTextRecommendationAndProfile.mutation';
import { UpdateTextRecommendationDocument } from '@/features/why-to-watch/graphql/mutations/UpdateTextRecommendation.mutation';

import Accordion from '@/pages/titleDetailV1/v1/content-gap-fillup/simfunia-components/Sim-Accordion.vue';
import BaseModalNew from '@/components/modals/BaseModalNew.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import InputText from '@/features/ui/components/InputText.vue';
import InputAvatar from '@/features/ui/components/InputAvatar.vue';
import InputTextArea from '@/features/ui/components/InputTextArea.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { isMobile } from '@/helpers/ui-helper';
import { toastError, toastSuccess } from '@/helpers/toast/toast-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { useMutation } from '@/helpers/composables/useApollo';
import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';

import { computed, ref } from 'vue';

import {
	CreateTextRecommendationDocument,
	CreateTextRecommendationMutation,
	CreateTextRecommendationMutationVariables,
} from '../graphql/mutations/CreateTextRecommendation.mutation';
import { faCheck, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { useWtwTracking } from '../tracking';

type Accordion = { openAccordion: () => void } | { closeAccordion: () => void } | null;


export default /*#__PURE__*/_defineComponent({
  __name: 'TextRecommendationModalV2',
  props: {
    title: null,
    formData: null,
    editMode: { type: Boolean, default: false }
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, formData?: Partial<TextRecommendationFragment> | null, editMode: boolean };





const userStore = useUserStore();
const { country, language } = useLanguageStore();
const { tc } = useI18n();

const isSubmitting = ref(false);

// TRACKING
const { trackWtw, trackWtwDisabledClick } = useWtwTracking({ title: () => props.title });

// Form Data
const id = ref<string>(props.formData?.id ?? '');
const headline = ref<string>(props.formData?.headline ?? '');
const watchIf = ref<string>(
	Array.isArray(props.formData?.watchIf) ? props.formData.watchIf.join('\n\n') : props.formData?.watchIf ?? ''
);
const skipIf = ref<string>(
	Array.isArray(props.formData?.skipIf) ? props.formData.skipIf.join('\n\n') : props.formData?.skipIf ?? ''
);
const body = ref<string>(props.formData?.body ?? '');

const accordionRefProfile = ref<Accordion>(null);
const accordionRefHeadline = ref<Accordion>(null);
const accordionRefWatchIf = ref<Accordion>(null);
const accordionRefSkipIf = ref<Accordion>(null);
const accordionRefBody = ref<Accordion>(null);

const fullName = ref<string>(userStore.userInfo.value.name ?? '');
const avatarBase64 = ref<string | null>(userStore.userInfo.value.photoURL ?? null);

const { isInSeenList } = useTitleDetailInfo(() => props.title);

const titleName = computed(() =>
	props.title.__typename === 'Season' && !!props.title.show
		? `${props.title.show.content.title} - ${props.title.content.title}`
		: props.title.content.title
);

const hasProfile = computed(() => {
	const profileUUID = userStore.profileInfo.value?.uuid;
	return profileUUID != null && profileUUID !== '';
});

const profileValidation = computed(() => fullName.value.length);
const headlineValidation = computed(() => headline.value.length && headline.value.length >= 10);
const watchIfValidation = computed(() => watchIf.value.length && watchIf.value.length >= 30);
const skipIfValidation = computed(() => skipIf.value.length && skipIf.value.length >= 30);
const bodyValidation = computed(() => body.value.length && body.value.length >= 60);

const submitValidation = computed(() => {
	const wtwValid =
		headlineValidation.value && watchIfValidation.value && skipIfValidation.value && bodyValidation.value;
	const profileValid = fullName.value.trim().length > 2;

	if (props.editMode) return wtwValid;

	return hasProfile.value ? wtwValid : profileValid && wtwValid;
});

const createRecommendationAndProfile = useMutation<
	CreateTextRecommendationAndProfileMutation,
	CreateTextRecommendationAndProfileMutationVariables
>(CreateTextRecommendationAndProfileDocument, {
	refetchQueries: ['GetTextRecommendations'],
});

const createRecommendation = useMutation<CreateTextRecommendationMutation, CreateTextRecommendationMutationVariables>(
	CreateTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

const updateRecommendation = useMutation<UpdateTextRecommendationMutation, UpdateTextRecommendationMutationVariables>(
	UpdateTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

function handleAvatarBase64(base64: string) {
	trackWtw('imageSelected');
	avatarBase64.value = base64;
}

function handleNext(accordion1: Accordion, accordion2: Accordion) {
	if (accordion1 && 'closeAccordion' in accordion1 && typeof accordion1.closeAccordion === 'function')
		accordion1.closeAccordion();
	if (accordion2 && 'openAccordion' in accordion2 && typeof accordion2.openAccordion === 'function')
		accordion2.openAccordion();
}

function toggleAccordion(accordionRef: Accordion) {
	const allAccordionRefs = [
		accordionRefProfile.value,
		accordionRefHeadline.value,
		accordionRefWatchIf.value,
		accordionRefSkipIf.value,
		accordionRefBody.value,
	];

	// Close all other accordions
	for (const ref of allAccordionRefs) {
		if (ref !== accordionRef && ref && 'closeAccordion' in ref && typeof ref.closeAccordion === 'function') {
			ref.closeAccordion();
		}
	}
}

async function handleSubmit() {
	if (!submitValidation.value || isSubmitting.value) {
		trackWtwDisabledClick();
		return;
	}

	isSubmitting.value = true;

	if (props.editMode) {
		await updateRecommendation.mutate({
			input: {
				id: id.value,
				headline: headline.value,
				watchIf: watchIf.value,
				skipIf: skipIf.value,
				body: body.value,
			},
			country: country.value,
			language: language.value,
		});
	} else {
		if (hasProfile.value) {
			await createRecommendation.mutate({
				input: {
					textRecommendation: {
						headline: headline.value,
						watchIf: watchIf.value,
						skipIf: skipIf.value,
						body: body.value,
						watchedOn: 0,
					},
					jwEntityID: props.title.id,
					country: country.value,
				},
				country: country.value,
				language: language.value,
			});
		} else {
			await createRecommendationAndProfile.mutate({
				input: {
					name: fullName.value,
					avatarBase64: avatarBase64.value,
					textRecommendation: {
						headline: headline.value,
						watchIf: watchIf.value,
						skipIf: skipIf.value,
						body: body.value,
						watchedOn: 0,
					},
					jwEntityID: props.title.id,
					country: country.value,
				},
				country: country.value,
				language: language.value,
			});
		}
	}

	isSubmitting.value = false;

	trackWtw('addWhyToWatch');
	emit('close');
}

function close() {
	trackWtw('modalClosed');
	emit('close');
}

updateRecommendation.onDone(() => onSuccess(tc('WEBAPP_RECOMMENDATION_UPDATED_SUCCESS')));
updateRecommendation.onError(error => {
	console.error(error);
	onError(tc('WEBAPP_RECOMMENDATION_UPDATED_ERROR'));
});

createRecommendation.onDone(() => onSuccess(tc('WEBAPP_RECOMMENDATION_ADDED_SUCCESS')));
createRecommendation.onError(error => {
	console.error(error);
	onError(tc('WEBAPP_RECOMMENDATION_ADDED_ERROR'));
});

createRecommendationAndProfile.onDone(() => onSuccess(tc('WEBAPP_RECOMMENDATION_ADDED_SUCCESS')));
createRecommendationAndProfile.onError(error => {
	console.error(error);
	onError(tc('WEBAPP_RECOMMENDATION_ADDED_ERROR'));
});

function onSuccess(message: string) {
	toastSuccess({ message, duration: 3000, position: isMobile() ? 'top' : 'bottom' });
}

function onError(message: string) {
	toastError({ message, duration: 3000, position: isMobile() ? 'top' : 'bottom' });
}

return { __sfc: true,props, emit, userStore, country, language, tc, isSubmitting, trackWtw, trackWtwDisabledClick, id, headline, watchIf, skipIf, body, accordionRefProfile, accordionRefHeadline, accordionRefWatchIf, accordionRefSkipIf, accordionRefBody, fullName, avatarBase64, isInSeenList, titleName, hasProfile, profileValidation, headlineValidation, watchIfValidation, skipIfValidation, bodyValidation, submitValidation, createRecommendationAndProfile, createRecommendation, updateRecommendation, handleAvatarBase64, handleNext, toggleAccordion, handleSubmit, close, onSuccess, onError, Accordion, BaseModalNew, BasicButton, InputText, InputAvatar, InputTextArea, PosterGraphql, FontAwesomeIcon, faCheck, faQuestionCircle }
}

})